/**
 * CEPA Client - API client for Connection Studio
 *
 * CEPA is now integrated into the mHive backend and uses the same
 * authentication system (Better Auth sessions).
 */

import { normalizeBackendUrl } from "@/lib/api-config";

// Types
export interface MasterCEPA {
  id: string;
  connection_id: string;
  name: string;
  version: string;
  status: "draft" | "generating" | "active" | "deprecated";
  openapi_spec?: Record<string, unknown>;
  client_code_path?: string;
  server_code_path?: string;
  pydantic_models_path?: string;
  mcp_tools?: MCPTool[];
  auth_template?: Record<string, unknown>;
  rate_limit_config?: RateLimitConfig;
  default_filters?: Record<string, unknown>;
  selected_paths?: string[];
  excluded_paths?: string[];
  selected_tags?: string[]; // OpenAPI tags to include in filtering
  openapi_spec_id?: string; // Reference to ng_openapi_specs record
  clone_count: number;
  last_generated_at?: string;
  generation_error?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CloneCEPA {
  id: string;
  master_cepa_id: string;
  data_space_id: string;
  name: string;
  auth_override?: Record<string, unknown>;
  allowed_paths?: string[];
  blocked_paths?: string[];
  custom_filters?: Record<string, unknown>;
  cache_ttl_seconds: number;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  read_only: boolean;
  sqlite_path?: string;
  chroma_collection?: string;
  duckdb_path?: string;
  synced_version?: string;
  last_synced_at?: string;
  sync_error?: string;
  last_used_at?: string;
  request_count: number;
  error_count: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface MCPTool {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
  endpoint_path?: string;
  method?: string;
}

export interface RateLimitConfig {
  requests_per_minute: number;
  requests_per_hour: number;
}

export interface CloneConfig {
  name: string;
  auth_override?: Record<string, unknown>;
  allowed_paths?: string[];
  blocked_paths?: string[];
  custom_filters?: Record<string, unknown>;
  cache_ttl_seconds?: number;
  rate_limit_per_minute?: number;
  rate_limit_per_hour?: number;
  read_only?: boolean;
}

export interface GenerationResult {
  success: boolean;
  master_cepa_id: string;
  version: string;
  client_code_path?: string;
  server_code_path?: string;
  pydantic_models_path?: string;
  mcp_tools_count: number;
  endpoints_count: number;
  error?: string;
  generated_at: string;
}

export interface GenerationAck {
  status: "generating";
  master_cepa_id: string;
  message: string;
}

export interface SyncResult {
  success: boolean;
  clone_id: string;
  master_version: string;
  previous_version?: string;
  changes: string[];
  error?: string;
  synced_at: string;
}

export interface PathInfo {
  path: string;
  methods: {
    method: string;
    summary: string;
    operation_id?: string;
    tags: string[];
  }[];
}

export interface FilterResult {
  success: boolean;
  original_paths_count: number;
  filtered_paths_count: number;
  removed_paths: string[];
  filtered_spec?: Record<string, unknown>;
  error?: string;
}

export interface CloneUsageStats {
  clone_id: string;
  request_count: number;
  error_count: number;
  last_used_at?: string;
  cache_hit_rate: number;
  avg_response_time_ms: number;
}

// API Client
export class CEPAClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl?: string) {
    // CEPA is now integrated into the backend - use the backend URL
    this.baseUrl =
      baseUrl || normalizeBackendUrl(process.env.NEXT_PUBLIC_BACKEND_URL);
    // Remove trailing slash if present
    this.baseUrl = this.baseUrl.replace(/\/$/, "");
  }

  /**
   * Set the auth token for API requests
   */
  setToken(token: string | null) {
    this.token = token;
  }

  private async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) || {}),
    };

    // Add auth token if available
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: "include", // Include cookies for session auth
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Master CEPA Methods
  async listMasterCepas(params?: {
    connection_id?: string;
    status?: string;
    show_all?: boolean;
  }): Promise<MasterCEPA[]> {
    const searchParams = new URLSearchParams();
    if (params?.connection_id)
      searchParams.set("connection_id", params.connection_id);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.show_all) searchParams.set("show_all", "true");

    const query = searchParams.toString();
    return this.fetch<MasterCEPA[]>(
      `/v1/master-cepas/${query ? `?${query}` : ""}`
    );
  }

  async getMasterCepa(id: string): Promise<MasterCEPA> {
    return this.fetch<MasterCEPA>(`/v1/master-cepas/${id}`);
  }

  async createMasterCepa(data: {
    connection_id: string;
    name: string;
    version?: string;
    selected_paths?: string[];
    excluded_paths?: string[];
    selected_tags?: string[];
    openapi_spec_id?: string;
    auth_template?: Record<string, unknown>;
    rate_limit_config?: RateLimitConfig;
  }): Promise<MasterCEPA> {
    return this.fetch<MasterCEPA>(`/v1/master-cepas/`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateMasterCepa(
    id: string,
    data: Partial<MasterCEPA>
  ): Promise<MasterCEPA> {
    return this.fetch<MasterCEPA>(`/v1/master-cepas/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async generateArtifacts(id: string): Promise<GenerationAck> {
    return this.fetch<GenerationAck>(`/v1/master-cepas/${id}/generate`, {
      method: "POST",
    });
  }

  async publishMasterCepa(
    id: string,
    version?: string
  ): Promise<{ success: boolean; message: string }> {
    const query = version ? `?version=${version}` : "";
    return this.fetch(`/v1/master-cepas/${id}/publish${query}`, {
      method: "POST",
    });
  }

  async deprecateMasterCepa(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    return this.fetch(`/v1/master-cepas/${id}/deprecate`, {
      method: "POST",
    });
  }

  async deleteMasterCepa(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    return this.fetch(`/v1/master-cepas/${id}`, {
      method: "DELETE",
    });
  }

  async getMcpTools(id: string): Promise<MCPTool[]> {
    return this.fetch<MCPTool[]>(`/v1/master-cepas/${id}/mcp-tools`);
  }

  async getArtifacts(id: string): Promise<{
    master_id: string;
    version: string;
    client_code_path?: string;
    server_code_path?: string;
    pydantic_models_path?: string;
    mcp_tools?: MCPTool[];
    last_generated_at?: string;
  }> {
    return this.fetch(`/v1/master-cepas/${id}/artifacts`);
  }

  // Clone CEPA Methods
  async listClones(params?: {
    master_cepa_id?: string;
    data_space_id?: string;
    show_all?: boolean;
  }): Promise<CloneCEPA[]> {
    const searchParams = new URLSearchParams();
    if (params?.master_cepa_id)
      searchParams.set("master_cepa_id", params.master_cepa_id);
    if (params?.data_space_id)
      searchParams.set("data_space_id", params.data_space_id);
    if (params?.show_all) searchParams.set("show_all", "true");

    const query = searchParams.toString();
    return this.fetch<CloneCEPA[]>(
      `/v1/clone-cepas/${query ? `?${query}` : ""}`
    );
  }

  async getClone(id: string): Promise<CloneCEPA> {
    return this.fetch<CloneCEPA>(`/v1/clone-cepas/${id}`);
  }

  async cloneToDataspace(
    master_cepa_id: string,
    data_space_id: string,
    config: CloneConfig
  ): Promise<CloneCEPA> {
    return this.fetch<CloneCEPA>(`/v1/clone-cepas/`, {
      method: "POST",
      body: JSON.stringify({
        master_cepa_id,
        data_space_id,
        config,
      }),
    });
  }

  /**
   * Quick create a clone directly from a connection
   * Creates master if needed, generates artifacts, and clones to dataspace
   */
  async createFromConnection(
    connection_id: string,
    data_space_id: string,
    name: string
  ): Promise<CloneCEPA> {
    const params = new URLSearchParams({
      connection_id,
      data_space_id,
      name,
    });
    return this.fetch<CloneCEPA>(
      `/v1/clone-cepas/from-connection?${params.toString()}`,
      {
        method: "POST",
      }
    );
  }

  async updateClone(
    id: string,
    data: Partial<CloneConfig>
  ): Promise<CloneCEPA> {
    return this.fetch<CloneCEPA>(`/v1/clone-cepas/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async syncClone(id: string): Promise<SyncResult> {
    return this.fetch<SyncResult>(`/v1/clone-cepas/${id}/sync`, {
      method: "POST",
    });
  }

  async deleteClone(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    return this.fetch(`/v1/clone-cepas/${id}`, {
      method: "DELETE",
    });
  }

  async listDataspaceClones(dataSpaceId: string): Promise<CloneCEPA[]> {
    return this.fetch<CloneCEPA[]>(
      `/v1/clone-cepas/data-spaces/${dataSpaceId}/cepas`
    );
  }

  async getCloneStats(id: string): Promise<CloneUsageStats> {
    return this.fetch<CloneUsageStats>(`/v1/clone-cepas/${id}/stats`);
  }

  // Legacy OpenAPI methods - these may need to be re-implemented
  // if you need them for the CEPA UI
  async getConnectionPaths(connectionId: string): Promise<{
    connection_id: string;
    total_paths: number;
    paths: PathInfo[];
  }> {
    // This endpoint may need to be added to the backend if needed
    return this.fetch(`/v1/cepa/connections/${connectionId}/spec`);
  }
}

// Singleton instance
let cepaClientInstance: CEPAClient | null = null;

export function getCEPAClient(): CEPAClient {
  if (!cepaClientInstance) {
    cepaClientInstance = new CEPAClient();
  }
  return cepaClientInstance;
}

/**
 * React hook for using CEPA client with auth token
 * Usage:
 *   const { client, isReady } = useCEPAClient();
 *   if (isReady) {
 *     const masters = await client.listMasterCepas();
 *   }
 */
export function createCEPAClientWithToken(token: string | null): CEPAClient {
  const client = new CEPAClient();
  client.setToken(token);
  return client;
}
